import { ROLE_OPTIONS, ROLE_STORED_VALUES } from '../services/userService';

// Notification Routing & Automation Recipients (roadmap 5.45) — data model.
//
// Per-company config for "who gets notified about what, and how". Stored in
// companies/{id}/meta/extras.notificationRouting, the same data-as-config home
// as costCategories / leadSources / sheetSpecOptions.
//
// SHAPE — a map keyed by EVENT KEY (never an array; the keys are a fixed
// vocabulary owned by the app, and a map lets a company store only the events
// it actually customized):
//
//   notificationRouting: {
//     taskAssigned: {
//       enabled: true,
//       recipients: [{ type: 'assignedRep' }, { type: 'role', role: 'operations' }],
//       method: 'both',
//     },
//     ...
//   }
//
// AN ABSENT KEY MEANS "use the shipped default" — resolved identically here and
// in functions/notificationRouting.js, so a company that never opens Settings
// still gets the smart defaults with zero configuration (Tesla philosophy), and
// the Cloud Functions never depend on a seed write having landed.
//
// ⚠ functions/notificationRouting.js carries a DELIBERATE MIRROR of the event
// keys, defaults, and role aliases below — INCLUDING the internal-scope events
// further down, which sit in the server's one DEFAULT_ROUTING map alongside the
// tenant ones. Cloud Functions cannot import from src/, and the alternative (a
// Firestore-hosted registry) would put the app's own vocabulary behind a
// network read. Change one, change the other.

// The four recipient kinds the owner can combine per event, per 5.45.
export const RECIPIENT_TYPES = {
  ASSIGNED_REP: 'assignedRep',
  OWNER: 'owner',
  ROLE: 'role',
  USER: 'user',
};

export const DELIVERY_METHODS = ['inApp', 'email', 'both'];

export const DELIVERY_METHOD_LABELS = {
  inApp: 'In-app',
  email: 'Email',
  both: 'Both',
};

// Roles offered in the "By role" picker: ROLE_OPTIONS itself, minus 'owner' —
// the owner is already its own recipient type, so listing it twice would be two
// controls for one person. DERIVED, not copied: this was a hand-kept duplicate
// and it outlived the list it mirrored by one role consolidation.
export const ROLE_RECIPIENT_OPTIONS = ROLE_OPTIONS.filter(r => r.value !== 'owner');

// A recipient rule stores a role id, and the members it has to reach may be
// stored under a pre-consolidation alias (see LEGACY_ROLE_ALIASES in
// userService.js). A notice that matched only the canonical id would silently
// miss anyone whose document predates the migration — so expand to every stored
// value that normalizes to the chosen role. Derived from the alias table rather
// than written out, so it cannot fall behind it.
export const ROLE_ALIASES = ROLE_STORED_VALUES;

export function rolesMatching(role) {
  return ROLE_ALIASES[role] || [role];
}

// Every event type, in the order 5.45 lists them. `builtOut: false` marks an
// event whose SOURCE feature doesn't exist yet — the trigger is wired and will
// fire the moment the feature starts writing its marker, but nothing produces
// that marker today (see functions/notificationRouting.js).
export const NOTIFICATION_EVENTS = [
  {
    key: 'fieldServiceSubmitted',
    name: 'New field service submitted',
    desc: 'A field technician submits a service issue from a job site.',
    builtOut: false,
    pendingNote: 'Waiting on the Field Service Mobile Submission flow (roadmap 5.44). Configure it now — it starts firing the day that ships.',
    defaults: {
      enabled: true,
      recipients: [{ type: 'owner' }, { type: 'role', role: 'operations' }],
      method: 'both',
    },
  },
  {
    key: 'taskAssigned',
    name: 'Task assigned',
    desc: 'A task is created for, or reassigned to, someone on the team.',
    builtOut: true,
    defaults: {
      enabled: true,
      recipients: [{ type: 'assignedRep' }],
      method: 'both',
    },
  },
  {
    key: 'stageChanged',
    name: 'Stage changed',
    desc: 'A job moves from one pipeline stage to another.',
    builtOut: true,
    defaults: {
      enabled: true,
      recipients: [{ type: 'assignedRep' }],
      method: 'inApp',
    },
  },
  {
    key: 'jobAssignedToRep',
    name: 'Job assigned to rep',
    desc: 'A job’s assigned rep changes.',
    builtOut: true,
    defaults: {
      enabled: true,
      recipients: [{ type: 'assignedRep' }],
      method: 'both',
    },
  },
  {
    key: 'taskOverdue',
    name: 'Task overdue',
    desc: 'An open task passes its due date. Checked once a day.',
    builtOut: true,
    defaults: {
      enabled: true,
      recipients: [{ type: 'assignedRep' }, { type: 'owner' }],
      method: 'both',
    },
  },
  {
    key: 'paymentReceived',
    name: 'Payment received',
    desc: 'An invoice is marked Paid.',
    builtOut: true,
    defaults: {
      enabled: true,
      recipients: [{ type: 'owner' }],
      method: 'inApp',
    },
  },
  {
    key: 'problemJobFlagged',
    name: 'Problem job flagged',
    desc: 'A job is flagged as a problem — stalled, over budget, or needing attention.',
    builtOut: false,
    pendingNote: 'Financials derives problem jobs on the fly today; nothing writes a lasting flag yet. Fires as soon as one does (roadmap 5.26 / 5.29).',
    defaults: {
      enabled: true,
      recipients: [{ type: 'owner' }, { type: 'role', role: 'operations' }],
      method: 'both',
    },
  },
];

// ── INTERNAL-SCOPE EVENTS ────────────────────────────────────────────────────
//
// Events that only ever fire against companies/boneflow-internal — BoneFlow's
// own company (roadmap 5.10). They live in the SAME routing structure and are
// resolved by the SAME server code (functions/notificationRouting.js carries
// them in DEFAULT_ROUTING alongside everything else), because the recipient
// resolution, delivery, audience trail and email builder are exactly what they
// need and a parallel routing path would be a second thing to keep correct.
//
// They are held in a SEPARATE list rather than added to NOTIFICATION_EVENTS for
// two concrete reasons:
//   • buildDefaultNotificationRouting() seeds every NEW tenant's extras from
//     that list, and writing "Bug report submitted" into a window-and-door
//     company's config would be storing a key that company can never act on.
//   • Settings → Notifications renders that list, and offering a customer a
//     switch over who at BoneFlow hears about bugs is worse than meaningless.
//
// The internal company DOES get the switch — Settings shows these events when,
// and only when, the signed-in company is the internal one (isInternalCompany).
// Until it is configured there, resolveEventConfig on the server falls back to
// the shipped default below, which is the same absent-means-default rule every
// other event follows.
export const INTERNAL_NOTIFICATION_EVENTS = [
  {
    key: 'bugReportSubmitted',
    name: 'Bug report submitted',
    desc: 'Someone using BoneFlow reports a bug or sends feedback from the in-app widget.',
    builtOut: true,
    scope: 'internal',
    defaults: {
      enabled: true,
      recipients: [{ type: 'owner' }],
      method: 'both',
    },
  },
];

// Everything the app knows about, tenant-facing and internal. Use this for
// LOOKUPS (what does this key mean?); use NOTIFICATION_EVENTS for anything that
// seeds or renders a tenant's own configuration.
export const ALL_NOTIFICATION_EVENTS = [...NOTIFICATION_EVENTS, ...INTERNAL_NOTIFICATION_EVENTS];

export const NOTIFICATION_EVENT_KEYS = NOTIFICATION_EVENTS.map(e => e.key);

// The event list a given company may configure: the shipped tenant events, plus
// the internal ones when this IS BoneFlow's own internal company.
//
// Takes the FLAG rather than the company object, and returns one of two
// module-level arrays. Both matter: callers can pass a primitive straight into
// a hook dependency list, and the returned identity is stable across renders,
// so a consumer that memoizes on it never churns.
export function notificationEventsForScope(isInternalCompany) {
  return isInternalCompany === true ? ALL_NOTIFICATION_EVENTS : NOTIFICATION_EVENTS;
}

export function getNotificationEvent(key) {
  return ALL_NOTIFICATION_EVENTS.find(e => e.key === key) || null;
}

export function buildDefaultNotificationRouting() {
  const out = {};
  NOTIFICATION_EVENTS.forEach(e => {
    out[e.key] = {
      enabled: e.defaults.enabled,
      recipients: e.defaults.recipients.map(r => ({ ...r })),
      method: e.defaults.method,
    };
  });
  return out;
}

// True when a company has no routing config at all. Keyed on the value being
// ABSENT, never on it being empty — same rule as the price book: a company that
// deliberately turned every event off must not have the defaults handed back on
// the next load.
export function notificationRoutingNeedsSeed(value) {
  return !value || typeof value !== 'object' || Array.isArray(value);
}

// Drop anything that isn't a recognizable recipient. Hand-edited or
// partially-written config can never reach the UI (or the senders) misshapen.
function normalizeRecipients(list) {
  if (!Array.isArray(list)) return [];
  const seen = new Set();
  const out = [];
  list.forEach(r => {
    if (!r || typeof r !== 'object') return;
    let key = null;
    let cleaned = null;
    if (r.type === RECIPIENT_TYPES.ASSIGNED_REP) {
      key = 'assignedRep';
      cleaned = { type: RECIPIENT_TYPES.ASSIGNED_REP };
    } else if (r.type === RECIPIENT_TYPES.OWNER) {
      key = 'owner';
      cleaned = { type: RECIPIENT_TYPES.OWNER };
    } else if (r.type === RECIPIENT_TYPES.ROLE && r.role) {
      key = `role:${r.role}`;
      cleaned = { type: RECIPIENT_TYPES.ROLE, role: String(r.role) };
    } else if (r.type === RECIPIENT_TYPES.USER && r.uid) {
      key = `user:${r.uid}`;
      cleaned = { type: RECIPIENT_TYPES.USER, uid: String(r.uid) };
    }
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push(cleaned);
  });
  return out;
}

// Stored config layered over the shipped defaults, per event. Unknown event
// keys are dropped (the app owns the vocabulary); a stored event missing a
// field falls back to that event's default for the field alone.
//
// `events` decides WHICH vocabulary applies, and defaults to the tenant-facing
// one so every existing caller is unchanged. The internal company passes
// notificationEventsForCompany(company) so its bugReportSubmitted config
// survives a normalize → save round trip instead of being dropped as unknown.
export function normalizeNotificationRouting(stored, events = NOTIFICATION_EVENTS) {
  const defaults = {};
  events.forEach(e => {
    defaults[e.key] = {
      enabled: e.defaults.enabled,
      recipients: e.defaults.recipients.map(r => ({ ...r })),
      method: e.defaults.method,
    };
  });
  if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return defaults;
  const out = {};
  events.forEach(e => {
    const d = defaults[e.key];
    const s = stored[e.key];
    if (!s || typeof s !== 'object') { out[e.key] = d; return; }
    out[e.key] = {
      enabled: s.enabled !== undefined ? s.enabled !== false : d.enabled,
      recipients: Array.isArray(s.recipients) ? normalizeRecipients(s.recipients) : d.recipients,
      method: DELIVERY_METHODS.includes(s.method) ? s.method : d.method,
    };
  });
  return out;
}

export function recipientKey(r) {
  if (!r) return '';
  if (r.type === RECIPIENT_TYPES.ROLE) return `role:${r.role}`;
  if (r.type === RECIPIENT_TYPES.USER) return `user:${r.uid}`;
  return r.type;
}

export function hasRecipient(recipients, candidate) {
  const key = recipientKey(candidate);
  return (recipients || []).some(r => recipientKey(r) === key);
}

// Human label for a chip. `users` is the company team list (uid/firstName/
// lastName/email) so a specific-user recipient reads as a name, not a uid.
export function recipientLabel(r, users) {
  if (!r) return '';
  if (r.type === RECIPIENT_TYPES.ASSIGNED_REP) return 'Assigned Rep';
  if (r.type === RECIPIENT_TYPES.OWNER) return 'Owner';
  if (r.type === RECIPIENT_TYPES.ROLE) {
    const opt = ROLE_RECIPIENT_OPTIONS.find(o => o.value === r.role);
    return opt ? opt.label : r.role;
  }
  const u = (users || []).find(x => x.uid === r.uid);
  if (!u) return 'Removed user';
  return `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email || 'Team member';
}

export function methodIncludesEmail(method) {
  return method === 'email' || method === 'both';
}

export function methodIncludesInApp(method) {
  return method === 'inApp' || method === 'both';
}
